<template>
  <div class="popover-wrapper" @focusout="onFocusOut" @keydown.esc="closeTooltip">
    <Dropdown
      v-model:shown="isShown"
      :placement="placement"
      :distance="distance"
      :container="false as any"
      popper-class="popover-modal"
      :overflow-padding="8"
    >
      <div ref="triggerWrapper" class="popover-wrapper__trigger">
        <slot name="trigger"></slot>
      </div>
      <template #popper>
        <div class="popover-modal-content">
          <button
            v-if="showCloseButton"
            class="popover-modal-content__x-btn"
            :aria-label="t('close')"
            type="button"
            @click="closeAndReturnFocus"
          >
            <i class="icon icon-Close" aria-hidden="true"></i>
          </button>
          <slot></slot>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { Dropdown } from 'floating-vue';
import { useI18n } from 'vue-i18n';
import { useTemplateRef } from 'vue';
import type { PopoverModalProps } from './types';

const { t } = useI18n();

withDefaults(defineProps<PopoverModalProps>(), {
  placement: 'bottom-end',
  distance: 10,
  showCloseButton: false,
});

const isShown = defineModel<boolean>('shown', { default: false });

const triggerWrapperRef = useTemplateRef('triggerWrapper');

const closeAndReturnFocus = () => {
  isShown.value = false;
  setTimeout(() => {
    triggerWrapperRef.value
      ?.querySelector<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }, 0);
};

const closeTooltip = (e: KeyboardEvent) => {
  if (!isShown.value) return;
  e.stopPropagation();
  closeAndReturnFocus();
};

const onFocusOut = (e: FocusEvent) => {
  const next = e.relatedTarget as Node | null;
  if (!next) return;

  if (!(e.currentTarget as HTMLElement).contains(next)) {
    isShown.value = false;
  }
};
</script>

<style lang="scss">
@import 'floating-vue/dist/style.css';
@import './scss/PopoverModal.scss';
</style>
